import { Filter, SelectionType } from './filter.js';
import { FilterOption } from './filter-option.js';
import { Article, Product } from '../../@types/wishlist.js';
import { FilterAction } from './filter-action.js';
import { SortAction } from './sort-action.js';
import { config } from '../service/config.js';

const REDUCTION_COUNTRIES_BLACKLIST = new Set([
  'PL',
  'IT',
  'BE',
  'NL',
  'LU',
  'ES',
  'FI',
  'SI',
  'LV',
  'LT',
  'EE',
  'IE',
  'PT',
  'GR',
  'HR',
  'BG',
  'SK',
]);

export default (products: Product[]) => {
  const sortRecentlyAdded = new FilterOption(
    'sort-recently-added',
    SortAction.createRecentlyAddedSortStrategy(),
    undefined,
    undefined,
    true,
  );
  const sortPriceDescending = new FilterOption(
    'sort-price-descending',
    SortAction.createDescSortStrategy(
      SortAction.createArticleCriteria((article: Article) => article.price.currentPriceInCent, Math.min, -1),
    ),
  );
  const sortPriceAscending = new FilterOption(
    'sort-price-ascending',
    SortAction.createAscSortStrategy(
      SortAction.createArticleCriteria(
        (article: Article) => article.price.currentPriceInCent,
        Math.min,
        Number.MAX_VALUE,
      ),
    ),
  );
  const sortSaleDescending = REDUCTION_COUNTRIES_BLACKLIST.has(config.country ?? '')
    ? undefined
    : new FilterOption(
        'sort-sale-descending',
        SortAction.createDescSortStrategy(
          SortAction.createArticleCriteria((article: Article) => article.price.reduction ?? 0, Math.max, -1),
        ),
      );
  const sortNewest = new FilterOption(
    'sort-newest',
    SortAction.createDescSortStrategy(
      SortAction.createArticleCriteria((article: Article) => article.firstSetActive, Math.min, -1),
    ),
  );

  const categoryOptions = Array.from(
    products
      .flatMap((product) => product.categories)
      .reduce((map, category) => {
        map.set(category.id, category);
        return map;
      }, new Map())
      .values(),
  )
    .map(
      (category) =>
        new FilterOption(
          category.id,
          new FilterAction((p) => p.categories.map((c) => c.id).includes(category.id)),
          category.name,
          category.isClothing ? 'clothing' : 'other',
        ),
    )
    .sort((a, b) => a.predefinedLabel!.localeCompare(b.predefinedLabel!));

  const inPromotion = new FilterOption(
    'in-promotion',
    FilterAction.createArticleBasedFilter(
      (article: Article, product?: Product) => (product?.isAnyArticleInPromotion ?? false) || article.isInPromotion,
    ),
    undefined,
    undefined,
    false,
    false,
    'ents_wishlist_filter_option_inPromotion_short',
  );
  const onSale = new FilterOption(
    'on-sale',
    FilterAction.createArticleBasedFilter((article: Article) => article.price.isReduced),
    undefined,
    undefined,
    false,
    true,
  );

  const isInStock = new FilterOption(
    'is-in-stock',
    FilterAction.createArticleBasedFilter((article: Article) => !article.isOutOfStock),
  );

  const isLowStock = new FilterOption(
    'is-low-stock',
    new FilterAction((product: Product) => {
      if (!product.articleId) {
        return false;
      }
      return product.articles.some(
        (article) => article.id === product.articleId && !article.isOutOfStock && article.lowStockMessage !== undefined,
      );
    }),
    undefined,
    undefined,
    false,
    true,
  );

  const isOutOfStock = new FilterOption(
    'is-out-of-stock',
    new FilterAction((product: Product) => {
      if (product.articleId) {
        return product.articles.some((article) => article.id === product.articleId && article.isOutOfStock);
      }
      return product.articles.every((article) => article.isOutOfStock);
    }),
  );

  const targetGroupOptions = ['women', 'men', 'girls', 'boys', 'babies'].map(
    (groupName) => new FilterOption(groupName, new FilterAction((p) => !!p.targetGroup?.includes(groupName))),
  );

  const brandOptions = products
    .filter((product, i) => products.findIndex((p) => p.brandNiceName === product.brandNiceName) === i)
    .map(
      (product) =>
        new FilterOption(
          product.brandNiceName,
          new FilterAction((p) => p.brandNiceName === product.brandNiceName),
          product.brandName,
        ),
    )
    .sort((a, b) => a.predefinedLabel!.localeCompare(b.predefinedLabel!));

  const filter = [
    new Filter(
      'sorting',
      SelectionType.SINGLE_SELECT,
      [sortRecentlyAdded, sortPriceDescending, sortPriceAscending, sortSaleDescending, sortNewest].filter(
        (element) => element !== undefined,
      ),
      true,
    ),
    new Filter('target_group_filter', SelectionType.MULTI_SELECT, targetGroupOptions),
    new Filter('category_filter', SelectionType.MULTI_SELECT, categoryOptions, false, ['clothing', 'other']),
    new Filter('sale_or_promotion_filter', SelectionType.MULTI_SELECT, [onSale, inPromotion]),
    new Filter('brand_filter', SelectionType.MULTI_SELECT, brandOptions, false, [], 0.5),
    new Filter('availability_filter', SelectionType.MULTI_SELECT, [isInStock, isLowStock, isOutOfStock]),
  ];

  return {
    filter,
  };
};
